import uuid from "uuid";
import { Feature, FEATURE } from "../../core/geo/types";
import { isPoint, isPolygon } from "../../core/geo/assertions";
import { Projection } from "../../common/types";
import GeoCalculationItem from "../dto/GeoCalculationItem";
import { GeoCalcFeatures, GeoCalcStyle } from "../types";
import { FeatureStyle, GeoCalcFeatureStyle, GeoCalcLabelStyle } from "../types";

const INPUT_GEOMETRY_TYPE = "INPUT";

function removeOutputsThatAreAlsoInputs(geoCalculation: GeoCalculationItem): Feature[] {
    if (!geoCalculation.geometry) {
        throw new Error("geoCalculation geometry is undefined");
    }
    const inputs = geoCalculation.geometry.features
        .filter((feature) => feature.properties.type === INPUT_GEOMETRY_TYPE)
        .map((feature) => feature.properties.name);
    return geoCalculation.geometry.features.filter(
        (feature) => feature.properties.type === INPUT_GEOMETRY_TYPE || !inputs?.includes(feature.properties.name)
    );
}

const getStyleByGeometryType = (feature: Feature, style: FeatureStyle) => {
    if (isPoint(feature.geometry)) {
        return style.Point;
    }
    if (isPolygon(feature.geometry)) {
        return style.Polygon;
    }
    return {};
};

const createFeatureCollection = (features: Feature[], calculationId: string, style: GeoCalcFeatureStyle) => {
    return features.map((feature) => {
        return {
            id: feature.id,
            calculationId: calculationId,
            type: FEATURE,
            geometry: feature.geometry,
            properties: {
                style:
                    feature.properties.type === INPUT_GEOMETRY_TYPE
                        ? { ...style.inputStyle, ...getStyleByGeometryType(feature, style.inputStyle) }
                        : { ...style.outputStyle, ...getStyleByGeometryType(feature, style.outputStyle) },
            },
        };
    });
};

const createLabelCollection = (features: Feature[], calculationId: string, style: GeoCalcLabelStyle) => {
    return features.map((feature) => {
        return {
            id: feature.id + "_text",
            calculationId: calculationId,
            type: FEATURE,
            geometry: feature.geometry,
            properties: {
                isText: true,
                style:
                    feature.properties.type === INPUT_GEOMETRY_TYPE
                        ? { ...style.inputStyle }
                        : { ...style.outputStyle },
                valueText: feature.properties.name,
            },
        };
    });
};

const getGeoCalculationCreationFunction = (type: string) => {
    if (type === "feature") {
        return createFeatureCollection;
    }
    return createLabelCollection;
};

const createGeoCalculationCollection = (
    previousFeatures: GeoCalcFeatures[],
    geoCalculations: GeoCalculationItem[],
    style: GeoCalcStyle,
    type: string
) => {
    const geoCalculationCreationFunction: Function = getGeoCalculationCreationFunction(type);
    return {
        type: "vector",
        featuresCrs: Projection.EPSG_3857,
        id: uuid(),
        features: [
            {
                type: "FeatureCollection",
                id: uuid(),
                features: [
                    ...previousFeatures,
                    ...geoCalculations.reduce(
                        (acc: GeoCalculationItem[], geoCalculation) =>
                            acc.concat(
                                geoCalculationCreationFunction(
                                    removeOutputsThatAreAlsoInputs(geoCalculation),
                                    geoCalculation.id,
                                    style
                                )
                            ),
                        []
                    ),
                ],
                properties: {},
            },
        ],
    };
};

export { createGeoCalculationCollection };
